import { logger } from './logger.ts';
import { supabase } from '../lib/db.ts';
import {
  buildPortfolioDataResult,
  PortfolioBet,
  PortfolioDataResult,
  MarketPoolRow,
  RawCreatedMarket,
} from './portfolioDataService.ts';
import type { WalletTransaction } from '../types.ts';

interface AdminUserOverviewRecord {
  id: string;
  email: string;
  role: string;
  created_at: string;
  last_sign_in_at: string | null;
  is_blocked: boolean;
  fica_verified: boolean | null;
  phone: string | null;
  is_sso_user: boolean;
  is_anonymous: boolean;
  wallet_balance: number | null;
  email_confirmed_at: string | null;
  raw_user_meta_data: Record<string, unknown> | null;
  referrer_id: string | null;
  referrer_name: string | null;
  referrer_email: string | null;
  referrer_affiliate_code: string | null;
}

export interface AdminUserOverview {
  id: string;
  email: string;
  role: string;
  createdAt: string;
  lastSignInAt: string | null;
  isBlocked: boolean;
  ficaVerified: boolean;
  phone: string | null;
  isSsoUser: boolean;
  isAnonymous: boolean;
  walletBalance: number;
  emailConfirmedAt: string | null;
  firstName: string;
  lastName: string;
  fullName: string;
  referrerId: string | null;
  referrerName: string | null;
  referrerEmail: string | null;
  referrerAffiliateCode: string | null;
}

export async function fetchAdminUserOverview(userId: string): Promise<AdminUserOverview | null> {
  try {
    const { data, error } = await supabase
      .rpc('admin_get_user_overview', { p_user_id: userId })
      .maybeSingle();

    if (error) {
      logger.error('Error fetching admin user overview:', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    const record = data as AdminUserOverviewRecord;
    const metadata = (record.raw_user_meta_data || {}) as Record<string, unknown>;

    const firstName = typeof metadata.first_name === 'string' ? metadata.first_name : '';
    const lastName = typeof metadata.last_name === 'string' ? metadata.last_name : '';
    const fullNameFromMeta = typeof metadata.name === 'string' ? metadata.name : '';
    const fullName = fullNameFromMeta || [firstName, lastName].filter(Boolean).join(' ').trim();

    return {
      id: record.id,
      email: record.email,
      role: record.role,
      createdAt: record.created_at,
      lastSignInAt: record.last_sign_in_at,
      isBlocked: Boolean(record.is_blocked),
      ficaVerified: Boolean(record.fica_verified),
      phone: record.phone,
      isSsoUser: Boolean(record.is_sso_user),
      isAnonymous: Boolean(record.is_anonymous),
      walletBalance: record.wallet_balance ?? 0,
      emailConfirmedAt: record.email_confirmed_at,
      firstName,
      lastName,
      fullName,
      referrerId: record.referrer_id,
      referrerName: record.referrer_name,
      referrerEmail: record.referrer_email,
      referrerAffiliateCode: record.referrer_affiliate_code,
    };
  } catch (err) {
    logger.error('Failed to fetch admin user overview:', err);
    throw err;
  }
}

export async function fetchAdminUserPortfolio(userId: string): Promise<PortfolioDataResult> {
  try {
    const { data, error } = await supabase
      .rpc('admin_get_user_portfolio', { p_user_id: userId })
      .single();

    if (error) {
      logger.error('Error fetching admin user portfolio:', error);
      throw error;
    }

    if (!data || typeof data !== 'object') {
      logger.warn('Admin user portfolio RPC returned empty result', { userId });
      return buildPortfolioDataResult(
        {
          bets: [],
          marketPools: [],
          transactions: [],
          createdMarkets: [],
          walletBalance: 0,
        },
        { includeWalletBalance: true }
      );
    }

    const payload = data as { admin_get_user_portfolio?: Record<string, unknown> };
    const raw = (payload.admin_get_user_portfolio ?? data) as Record<string, unknown>;

    const bets = Array.isArray(raw.bets) ? (raw.bets as PortfolioBet[]) : [];
    const marketPools = Array.isArray(raw.market_pools)
      ? (raw.market_pools as MarketPoolRow[])
      : [];
    const transactions = Array.isArray(raw.transactions)
      ? (raw.transactions as WalletTransaction[])
      : [];
    const createdMarkets = Array.isArray(raw.created_markets)
      ? (raw.created_markets as RawCreatedMarket[])
      : [];
    const walletBalance = typeof raw.wallet_balance === 'number' ? raw.wallet_balance : null;

    return buildPortfolioDataResult(
      {
        bets,
        marketPools,
        transactions,
        createdMarkets,
        walletBalance,
      },
      { includeWalletBalance: true }
    );
  } catch (err) {
    logger.error('Failed to fetch admin user portfolio:', err);
    throw err;
  }
}

export interface AffiliateUser {
  id: string;
  email: string;
  username: string;
  affiliate_code: string | null;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
}

export async function fetchAdminUserAffiliates(userId: string): Promise<AffiliateUser[]> {
  try {
    const { data, error } = await supabase
      .rpc('admin_get_user_affiliates', { p_user_id: userId });

    if (error) {
      logger.error('Error fetching admin user affiliates:', error);
      throw error;
    }

    if (!data || !Array.isArray(data)) {
      return [];
    }

    return data as AffiliateUser[];
  } catch (err) {
    logger.error('Failed to fetch admin user affiliates:', err);
    throw err;
  }
}

